'use client'

import { useTranslations } from 'next-intl'

import useUpdateEffect from '../../hooks/update-effect'
import Select from '../../primitives/components/select'
import { faArrowCircleRight, faArrowCircleLeft } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Button } from '@radix-ui/themes'

import { useTableContext, useTableStore } from '../context/table-context'
import { StyledFooter } from '../styled'

export const generatePageSizes = (total: number, defaultPageSize: number) => {
    const sizes = [10, 25]
    let last = 25

    while (last < total) {
        last *= 2
        sizes.push(last)
    }

    return sizes.filter(
        (opt, index, originalArray) =>
            opt <= total ||
            index == 0 ||
            originalArray[index - 1] < total ||
            opt <= defaultPageSize
    ).map(opt => ({ value: opt, label: opt }))
}

const TableFooter = <T,>() => {
    const { defaultPageSize, hidePagination, store } = useTableContext<T>()
    const total = useTableStore('total')
    const refreshData = useTableStore('refreshData')
    const pagination = useTableStore('pagination')
    const options = generatePageSizes(total, defaultPageSize)

    const t = useTranslations('table.pagination')

    const setPagination = (newPagination: { page: number; pageSize: number }) =>
        store.update({ pagination: newPagination })

    useUpdateEffect(() => {
        refreshData()
    }, [pagination])

    if (hidePagination)
        return null

    const lastPage = (pagination.page + 1) * pagination.pageSize >= total

    return (
        <StyledFooter>
            <span>
                {`${Math.min(pagination.page * pagination.pageSize + 1, total)}-${Math.min((pagination.page + 1) * pagination.pageSize, total)}`}{' '}
                {t('of')} {total}
            </span>

            <Select
                options={options}
                // a different page size invalidates the page index, start from the first page
                onChange={newValue => setPagination({ page: 0, pageSize: +newValue! })}
                defaultValue={pagination.pageSize}
                menuPlacement="top"
            />

            <Button
                variant="ghost"
                disabled={pagination.page == 0 ? true : undefined}
                onClick={() => {
                    if (pagination.page == 0)
                        return

                    setPagination({ ...pagination, page: pagination.page - 1 })
                }}
            >
                <FontAwesomeIcon icon={faArrowCircleLeft} />
            </Button>

            <Button
                variant="ghost"
                disabled={lastPage ? true : undefined}
                onClick={() => {
                    if (lastPage)
                        return

                    setPagination({ ...pagination, page: pagination.page + 1 })
                }}
            >
                <FontAwesomeIcon icon={faArrowCircleRight} />
            </Button>
        </StyledFooter>
    )
}

export default TableFooter
